import { hueOf, selectRoster, useBoard, workspaces } from "../lib/board";
import { liveActorsOf } from "../lib/roster.mjs";
import { bridge } from "../lib/bridge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import type { CSSProperties, ReactNode } from "react";

function WsItem({ repoName, selected, onSelect, children }: { repoName: string; selected: boolean; onSelect: () => void; children: ReactNode }) {
  const roster = useBoard(selectRoster);
  const inRepo = liveActorsOf(roster, repoName);
  return (
    <button className="ws-item" data-sel={String(selected)} data-live={String(inRepo.length > 0)} onClick={onSelect}>
      {children}
      {repoName
        ? inRepo.map((r) => (
            <span key={r.actor} className="livedot" style={{ "--who": hueOf(r.actor) } as CSSProperties} title={r.actor} />
          ))
        : null}
    </button>
  );
}

/** The two items that are not a folder. Prefixed so they can never collide
 *  with a real directory path, which is what every other value here is. */
const ADD = " add";
const CLOSE = " close";

/**
 * The open folder, and a way to change it.
 *
 * ⚠️ A LIST THAT GROWS FOREVER. Andrew: "in the bottom right there are all of
 * the added repos and they stack. instead it should be a dropdown once there
 * are two or more." Every folder anybody has ever opened was a permanent two-
 * line row at the bottom of the rail, so the rail's last section grew without
 * limit and pushed the useful parts of it up and out.
 *
 * ⚠️ ALWAYS THE DROPDOWN, even for one folder or none. It used to fall back to
 * stacked rows below two, which meant the rail's bottom corner changed shape
 * as you opened folders — and the "Repos" header above it existed only to
 * label that shifting thing. Andrew: "get rid of the repo header and have the
 * dropdown just start with open a repo, and then in the dropdown are all the
 * repos". One control, one row, whatever is open.
 *
 * What the rows had and this keeps: who is live in each repo (the coloured
 * dots), and closing the open one — which used to be "click the selected row
 * again", an interaction nothing announced. It is an item now.
 */
export function WorkspacePicker() {
  const localRoot = useBoard((s) => s.localRoot);
  const localWorkspaces = useBoard((s) => s.localWorkspaces);
  const openLocalRoot = useBoard((s) => s.openLocalRoot);
  const unsetLocalRoot = useBoard((s) => s.unsetLocalRoot);
  const addWorkspace = useBoard((s) => s.addWorkspace);
  const roster = useBoard(selectRoster);
  const list = localWorkspaces || [];
  const open = list.find((w) => w.dir === localRoot);

  return (
    <Select
      value={localRoot || ""}
      onValueChange={(v: string | null) => {
        if (!v) return;
        if (v === ADD) return void addWorkspace();
        if (v === CLOSE) return unsetLocalRoot();
        if (v === localRoot) return;
        openLocalRoot(v);
      }}
    >
      <SelectTrigger className="follow ws-pick" size="sm" aria-label="Open folder">
        <SelectValue>{() => (open ? open.name : "Open a folder")}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {list.map((w) => {
          const inRepo = liveActorsOf(roster, w.repo || w.name);
          return (
            <SelectItem value={w.dir} key={w.dir}>
              <span className="ws-pick-row">
                <span>{w.name}</span>
                {inRepo.map((r) => (
                  <span
                    key={r.actor}
                    className="livedot"
                    style={{ "--who": hueOf(r.actor) } as CSSProperties}
                    title={r.actor}
                  />
                ))}
              </span>
            </SelectItem>
          );
        })}
        <SelectItem value={ADD}>Open a folder…</SelectItem>
        {open ? <SelectItem value={CLOSE}>Close {open.name}</SelectItem> : null}
      </SelectContent>
    </Select>
  );
}

export function WorkspacesPane() {
  const selectedRepo = useBoard((s) => s.selectedRepo);
  const toggleRepo = useBoard((s) => s.toggleRepo);

  /* ⚠️ NOTHING AT ALL ON THE DESKTOP. The folder picker moved into the strip,
     beside the branch it is the subject of — Andrew: "get rid of the zevet
     thing with a dropdown on the bottom left, and just put it to the left of
     main in that block above". One row of the rail is reclaimed, and the repo
     stops being named in two places that could disagree.

     This component still exists because the BROWSER case is a different
     feature wearing the same name: with no desktop bridge there is no folder
     to open, and this is the repo FILTER over hub events. That has not moved
     and should not. */
  if (bridge.local) return null;

  const list = workspaces();
  return (
    <div className="ws" id="workspaces">
      {list.map((w) => (
        <WsItem key={w.repo} repoName={w.repo} selected={selectedRepo === w.repo} onSelect={() => toggleRepo(w.repo)}>
          <span>{w.repo}</span>
          {w.branch ? (
            <>
              <br />
              <span className="branch">{w.branch}</span>
            </>
          ) : null}
        </WsItem>
      ))}
      {!list.length ? <div style={{ color: "var(--subtle)", fontSize: "12.5px" }}>none yet</div> : null}
    </div>
  );
}